"use client"

import { usePathname } from "next/navigation"
import { Header } from "@/components/ui/header"
import { Footer } from "@/components/ui/footer"
import { RouteScrollRestoration } from "@/components/ui/route-scroll-restoration"
import { KonamiGameLauncher } from "@/components/games/konami-game-launcher"

interface LayoutWrapperProps {
  children: React.ReactNode
}

function SkipToMainContent() {
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:absolute focus:z-100 focus:top-4 focus:left-4 focus:px-4 focus:py-2 focus:bg-white focus:text-green-700 focus:rounded-md focus:shadow-lg focus:ring-2 focus:ring-green-500 focus:outline-hidden"
    >
      Skip to main content
    </a>
  )
}

export function LayoutWrapper({ children }: LayoutWrapperProps) {
  const pathname = usePathname()
  const isEmbedRoute = pathname?.startsWith('/embed')
  const isAdminRoute = pathname?.startsWith('/admin')
  const isDashboardRoute = pathname?.startsWith('/dashboard')
  const isAuthRoute = pathname?.startsWith('/auth')
  const isAdLandingRoute = pathname === '/download'
  // The /games index is a normal chrome page; only the full-viewport game
  // canvases under /games/* own their shell.
  const isGameRoute = pathname?.startsWith('/games/')
  const isHomeRoute = pathname === '/'

  // Auth pages keep minimal chrome, but still share the site-level keyboard
  // skip link. Each auth page owns its single main landmark so Suspense and
  // state-specific screens stay inside the same target.
  if (isAuthRoute) {
    return (
      <>
        <SkipToMainContent />
        {children}
      </>
    )
  }

  if (isAdLandingRoute) {
    return (
      <>
        <SkipToMainContent />
        {children}
      </>
    )
  }

  // Embed, admin, dashboard, and game routes own their application shell.
  if (isEmbedRoute || isAdminRoute || isDashboardRoute || isGameRoute) {
    return <>{children}</>
  }

  // Main site gets full chrome
  // Use flex column layout to ensure proper vertical stacking and footer positioning
  return (
    <div className={`flex flex-col min-h-screen w-full max-w-full pb-ad-dock ${isHomeRoute ? 'bg-green-50' : ''}`}>
      <SkipToMainContent />
      <RouteScrollRestoration />
      <Header />
      <main id="main-content" className="flex-1 w-full max-w-full" tabIndex={-1}>
        {children}
      </main>
      <Footer />
      {/* Below-the-footer slot for page-level fine print (image attribution).
          Pages portal into this via <PageBottomPortal> — see that component. */}
      <div id="page-bottom-slot" className="w-full" />
      <KonamiGameLauncher />
    </div>
  )
}
